import { DEMO_ENTITY } from '../catalogs/banks.js';
import { ID_PREFIXES, toPublicId } from '../common/ids.js';
import { buildEvent } from '../events/envelope.js';
import { DEMO_LENDER_PROFILE } from '../identity/lender.js';
/**
 * Generadores de datos de ejemplo **válidos** (pasan los esquemas Zod) para MSW, Storybook y pruebas.
 * Deterministas: mismos argumentos → mismos datos. Las cifras financieras son de ejemplo fijo
 * (tomadas de `04-api-contratos.md` §6.1), no se calculan aquí.
 */
export const FIXTURE_NOW = new Date('2026-10-30T16:10:00.000Z');
export function fixedClock(at = FIXTURE_NOW) {
    return { now: () => new Date(at.getTime()) };
}
/** UUIDv7 determinista a partir de un número de secuencia. */
export function fixtureUuid(seq, at = FIXTURE_NOW) {
    const ms = at.getTime().toString(16).padStart(12, '0');
    const tail = seq.toString(16).padStart(18, '0').slice(-18);
    const hex = `${ms}7${tail.slice(0, 3)}8${tail.slice(3, 6)}${tail.slice(6)}`;
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20, 32)}`;
}
export function fixtureId(kind, seq = 1) {
    return toPublicId(ID_PREFIXES[kind], fixtureUuid(seq));
}
const cop = (amount) => ({ amount, currency: 'COP' });
const iso = (offsetMs = 0) => new Date(FIXTURE_NOW.getTime() + offsetMs).toISOString();
export function fixtureCreditLine(overrides = {}) {
    return {
        id: fixtureId('creditLine'),
        productCode: 'MICRO_ROTATIVO',
        productName: `Microcrédito Rotativo ${DEMO_ENTITY.name} (demo)`,
        productVersion: 1,
        approvedLimit: cop('2000000.00'),
        availableLimit: cop('1915000.00'),
        usedPrincipal: cop('85000.00'),
        heldAmount: cop('0.00'),
        creditBalance: cop('0.00'),
        minimumPayment: cop('30612.00'),
        payoffAmount: cop('85000.00'),
        status: 'ACTIVE',
        blockReason: null,
        dpd: 0,
        rating: 'A',
        openedAt: iso(-30 * 86_400_000),
        nextCutDate: '2026-11-05',
        nextDueDate: '2026-11-20',
        lastStatementId: null,
        billingFrequency: 'MONTHLY',
        ...overrides,
    };
}
export function fixtureSimulation(overrides = {}) {
    return {
        amount: cop('85000.00'),
        installments: 3,
        installmentAmount: cop('30612.00'),
        rateEA: '0.452100',
        rateMV: '0.031561',
        moratoriumRateEA: '0.452100',
        lifeInsuranceMonthlyRate: '0.000500',
        mipymeFee: cop('0.00'),
        insurance: cop('85.00'),
        fees: cop('0.00'),
        totalInterest: cop('6751.00'),
        totalInsurance: cop('85.00'),
        totalFees: cop('0.00'),
        totalCost: cop('91836.00'),
        totalToPay: cop('91836.00'),
        firstDueDate: '2026-12-05',
        schedule: [
            {
                number: 1,
                dueDate: '2026-12-05',
                openingBalance: cop('85000.00'),
                interest: cop('2683.00'),
                principal: cop('27929.00'),
                insurance: cop('43.00'),
                fee: cop('0.00'),
                installment: cop('30612.00'),
                total: cop('30655.00'),
            },
            {
                number: 2,
                dueDate: '2027-01-05',
                openingBalance: cop('57071.00'),
                interest: cop('1801.00'),
                principal: cop('28811.00'),
                insurance: cop('29.00'),
                fee: cop('0.00'),
                installment: cop('30612.00'),
                total: cop('30641.00'),
            },
            {
                number: 3,
                dueDate: '2027-02-05',
                openingBalance: cop('28260.00'),
                interest: cop('892.00'),
                principal: cop('28260.00'),
                insurance: cop('13.00'),
                fee: cop('0.00'),
                installment: cop('29152.00'),
                total: cop('29165.00'),
            },
        ],
        productVersion: 1,
        usuryRateEA: '0.570000',
        disclaimer: 'Datos de ejemplo (fixture). Las cifras reales las calcula el core de crédito.',
        simulatedAt: iso(),
        ...overrides,
    };
}
export function fixturePayee(overrides = {}) {
    return {
        id: fixtureId('payee'),
        name: 'Distribuidora La 14 SAS',
        aliasType: 'MERCHANT_CODE',
        aliasValue: '0012345678',
        participantCode: '07',
        participantName: 'BANCOLOMBIA',
        mcc: '5411',
        city: 'Bogotá',
        ...overrides,
    };
}
export function fixturePaymentIntent(overrides = {}) {
    return {
        id: fixtureId('paymentIntent'),
        status: 'AWAITING_SCA',
        customerId: fixtureId('customer'),
        lineId: fixtureId('creditLine'),
        payee: fixturePayee(),
        amount: cop('85000.00'),
        installments: 3,
        description: 'Pedido gaseosas',
        channel: 'APP',
        agentSessionId: null,
        fundingMode: 'SETTLEMENT_ACCOUNT',
        postingStatus: 'NOT_APPLICABLE',
        simulation: fixtureSimulation(),
        scaChallengeId: fixtureId('scaChallenge'),
        reasonCode: null,
        qrReference: null,
        authCode: null,
        endToEndId: null,
        drawdownId: null,
        createdAt: iso(),
        expiresAt: iso(10 * 60_000),
        completedAt: null,
        ...overrides,
    };
}
/** Comprobante de una compra completada (cifras de ejemplo de `fixtureSimulation`). */
export function fixtureReceipt(overrides = {}) {
    const simulation = fixtureSimulation();
    return {
        paymentIntentId: fixtureId('paymentIntent'),
        receiptNumber: 'PZ-20261030-000123',
        payee: fixturePayee(),
        amount: simulation.amount,
        installments: simulation.installments,
        installmentAmount: simulation.installmentAmount,
        rateEA: simulation.rateEA,
        rateMV: simulation.rateMV,
        authCode: 'A1B2C3',
        endToEndId: 'E0672026103016100000000123',
        drawdownId: fixtureId('drawdown'),
        completedAt: iso(42_000),
        receiptUrl: `/v1/receipts/${fixtureId('paymentIntent')}`,
        description: 'Pedido gaseosas',
        payer: {
            name: 'Rosa Martínez',
            participantName: DEMO_LENDER_PROFILE.legalName,
            maskedAccount: '****4321',
        },
        lender: { legalName: DEMO_LENDER_PROFILE.legalName, nit: DEMO_LENDER_PROFILE.nit },
        ...overrides,
    };
}
/**
 * Extracto de ejemplo con todo el contenido exigido por la SFC (cifras fijas de ejemplo, no
 * calculadas): una compra a 6 cuotas facturada, una compra nueva a 3 cuotas y un pago.
 */
export function fixtureStatement(overrides = {}) {
    const lineId = fixtureId('creditLine');
    const dd1 = fixtureId('drawdown', 1);
    const dd2 = fixtureId('drawdown', 2);
    return {
        id: fixtureId('statement'),
        lineId,
        periodStart: '2026-10-05',
        cutoffDate: '2026-11-05',
        dueDate: '2026-11-20',
        openingBalance: cop('616429.00'),
        purchases: cop('350000.00'),
        payments: cop('116429.00'),
        interest: cop('24865.00'),
        moratorium: cop('0.00'),
        insurance: cop('425.00'),
        fees: cop('0.00'),
        minimumPayment: cop('234844.00'),
        payoffAmount: cop('880290.00'),
        lines: [
            {
                drawdownId: dd1,
                description: 'Lácteos del Campo · cuota 2 de 6',
                installmentNumber: 2,
                principal: cop('96540.00'),
                interest: cop('15723.00'),
                insurance: cop('250.00'),
                fee: cop('0.00'),
                total: cop('112513.00'),
                excludedFromMinimum: false,
            },
            {
                drawdownId: dd2,
                description: 'Mayorista del Sur · cuota 1 de 3',
                installmentNumber: 1,
                principal: cop('113014.00'),
                interest: cop('9142.00'),
                insurance: cop('175.00'),
                fee: cop('0.00'),
                total: cop('122331.00'),
                excludedFromMinimum: false,
            },
        ],
        pdfUrl: null,
        holder: { fullName: 'Rosa Martínez', documentType: 'CC', maskedDocument: '****1222' },
        product: { code: 'MICRO_ROTATIVO', name: 'Microcrédito rotativo (demo)', version: 1 },
        pastDueAmount: cop('0.00'),
        daysPastDue: 0,
        creditBalance: cop('0.00'),
        deferredBalance: cop('645446.00'),
        creditLimit: {
            approvedLimit: cop('3000000.00'),
            usedPrincipal: cop('855000.00'),
            heldAmount: cop('0.00'),
            availableLimit: cop('2145000.00'),
        },
        rates: {
            rateEA: '0.450000',
            rateMV: '0.031447',
            moratoriumRateEA: '0.570000',
            moratoriumRateMV: '0.038294',
            usury: {
                modality: 'MICROCREDITO',
                validFrom: '2026-10-01',
                validTo: '2026-12-31',
                rateEA: '0.570000',
            },
        },
        transactions: [
            {
                date: '2026-10-10',
                type: 'PURCHASE',
                direction: 'CHARGE',
                description: 'Compra en Mayorista del Sur',
                merchantName: 'Mayorista del Sur',
                drawdownId: dd2,
                reference: fixtureId('paymentIntent', 2),
                amount: cop('350000.00'),
                installments: 3,
                rateEA: '0.450000',
            },
            {
                date: '2026-10-15',
                type: 'PAYMENT',
                direction: 'CREDIT',
                description: 'Pago de tu cuota',
                merchantName: null,
                drawdownId: null,
                reference: null,
                amount: cop('116429.00'),
                installments: null,
                rateEA: null,
            },
        ],
        installmentPlans: [
            {
                drawdownId: dd1,
                description: 'Lácteos del Campo',
                purchaseDate: '2026-09-15',
                originalAmount: cop('600000.00'),
                installments: 6,
                paidInstallments: 1,
                pendingInstallments: 5,
                billedInstallment: 2,
                installmentAmount: cop('112263.00'),
                rateEA: '0.450000',
                rateMV: '0.031447',
                outstandingPrincipal: cop('505000.00'),
                deferredPrincipal: cop('408460.00'),
                inDispute: false,
            },
            {
                drawdownId: dd2,
                description: 'Mayorista del Sur',
                purchaseDate: '2026-10-10',
                originalAmount: cop('350000.00'),
                installments: 3,
                paidInstallments: 0,
                pendingInstallments: 3,
                billedInstallment: 1,
                installmentAmount: cop('122156.00'),
                rateEA: '0.450000',
                rateMV: '0.031447',
                outstandingPrincipal: cop('350000.00'),
                deferredPrincipal: cop('236986.00'),
                inDispute: false,
            },
        ],
        generatedAt: '2026-11-05T05:00:00.000Z',
        ...overrides,
    };
}
/** Solo el contenido SFC del extracto de ejemplo (para completar extractos de otros mocks). */
export function fixtureStatementDetails() {
    const { holder, product, pastDueAmount, daysPastDue, creditBalance, deferredBalance, creditLimit, rates, transactions, installmentPlans, generatedAt, } = fixtureStatement();
    return {
        holder,
        product,
        pastDueAmount,
        daysPastDue,
        creditBalance,
        deferredBalance,
        creditLimit,
        rates,
        transactions,
        installmentPlans,
        generatedAt,
    };
}
/** Extracto tal como lo entrega `/v1` (con la identificación legal de la entidad). */
export function fixtureCustomerStatement(overrides = {}) {
    return { ...fixtureStatement(), lender: DEMO_LENDER_PROFILE, ...overrides };
}
export function fixtureDispute(overrides = {}) {
    return {
        id: fixtureId('dispute'),
        paymentIntentId: fixtureId('paymentIntent'),
        customerId: fixtureId('customer'),
        reason: 'NOT_RECEIVED',
        description: 'El pedido no llegó completo.',
        amount: cop('85000.00'),
        status: 'OPEN',
        decision: null,
        decidedAmount: null,
        decisionChannel: null,
        reversal: null,
        dueAt: iso(21 * 86_400_000),
        createdAt: iso(),
        ...overrides,
    };
}
export function fixtureCustomerProfile(overrides = {}) {
    return {
        id: fixtureId('customer'),
        fullName: 'Rosa Elena Martínez',
        documentType: 'CC',
        maskedDocument: '******4567',
        phone: '+573001234567',
        email: null,
        businessName: 'Tienda Doña Rosa',
        status: 'ACTIVE',
        linkedAccounts: [
            {
                id: 'acc_demo_1',
                participantCode: '990',
                participantName: DEMO_ENTITY.legalName,
                accountType: 'SAVINGS',
                maskedNumber: '****4321',
                creditLineId: fixtureId('creditLine'),
                isDefault: true,
            },
        ],
        hasActiveLine: true,
        activeApplicationId: null,
        preferences: { voiceEnabled: true, notifications: { push: true, sms: true, whatsapp: false } },
        ...overrides,
    };
}
export function fixtureEvidenceDocument(overrides = {}) {
    return {
        id: fixtureId('document'),
        applicationId: fixtureId('application'),
        declaredType: 'SALES_NOTEBOOK',
        detectedType: 'SALES_NOTEBOOK',
        status: 'ACCEPTED',
        mimeType: 'image/jpeg',
        sizeBytes: 482_113,
        pageCount: 1,
        uploadedVia: 'CHAT',
        review: {
            version: 1,
            reviewerType: 'AGENT',
            detectedType: 'SALES_NOTEBOOK',
            legibility: 'GOOD',
            validity: 'VALID',
            relevance: 'HIGH',
            extracted: { periodFrom: '2026-09-01', periodTo: '2026-09-30', totalSales: '4850000.00' },
            issues: [],
            fraudSignals: [],
            suggestions: [],
            verdict: 'ACCEPTED',
            reviewedAt: iso(60_000),
        },
        downloadUrl: null,
        createdAt: iso(),
        ...overrides,
    };
}
export function fixtureCreditApplication(overrides = {}) {
    return {
        id: fixtureId('application'),
        customerId: fixtureId('customer'),
        productCode: 'MICRO_ROTATIVO',
        phase: 'EVIDENCE',
        status: 'IN_PROGRESS',
        requestedAmount: cop('2000000.00'),
        purpose: 'Surtir la tienda para diciembre',
        businessProfile: {
            businessName: 'Tienda Doña Rosa',
            businessType: 'TIENDA',
            businessAgeMonths: 36,
            municipalityCode: '11001',
            premises: 'HOME_BASED',
        },
        householdProfile: { dependents: 2, householdSize: 4, housing: 'FAMILY' },
        completeness: '55.00',
        checklist: [
            {
                code: 'ID',
                label: 'Cédula (frente y respaldo)',
                phase: 'IDENTITY',
                status: 'DONE',
                required: true,
            },
            {
                code: 'SALES',
                label: 'Un soporte de ventas',
                phase: 'EVIDENCE',
                status: 'IN_REVIEW',
                required: true,
            },
        ],
        documents: [fixtureEvidenceDocument()],
        offer: null,
        decision: null,
        formalization: null,
        nextSteps: ['Sube una foto de tu cuaderno de ventas del último mes.'],
        agentSessionId: fixtureId('agentSession'),
        lastActivityAt: iso(),
        createdAt: iso(-86_400_000),
        ...overrides,
    };
}
export function fixtureHandoffCard() {
    return {
        id: fixtureId('agentCard'),
        type: 'HUMAN_HANDOFF',
        createdAt: iso(),
        expiresAt: null,
        reason: 'Un asesor revisará tus documentos.',
        expectedWait: '15 minutos',
    };
}
export function fixtureEvent(type, data, subjectId) {
    return buildEvent(type, data, {
        clock: fixedClock(),
        tenantCode: 'paguz-demo',
        subjectId,
        traceId: '4bf92f3577b34da6a3ce929d0e0e4736',
    });
}
//# sourceMappingURL=index.js.map